/**
 * Past visibility scans and how the rank metrics moved.
 *
 * Two things the UI must not get wrong, both decided on the backend and only
 * rendered here:
 *
 * - **Direction is not uniform.** ARP and ATRP are rank positions, so lower is
 *   better; SoLV is a share, so higher is. The `improved` flag carries the
 *   verdict; re-deriving it from the sign of `change` would paint half of
 *   every improvement red.
 * - **Not every pair is comparable.** A scan is only compared with an earlier
 *   scan of the same keyword, grid and radius. When there is no comparable
 *   one, this shows a dash — not a zero, and not a green tick.
 */
import { useQuery } from "@tanstack/react-query";
import { ArrowDown, ArrowRight, Minus } from "lucide-react";
import { ArrowUp } from "lucide-react";

import { Button } from "@/components/ui/button";

import {
	gbpAuditService,
	type GbpScan,
	type GbpScanDelta,
	type GbpScanHistory as ScanHistory,
} from "@/services/gbpAuditService";

interface Props {
	tenantId: string;
	placeId?: string;
	/**
	 * Show this scan's analysis. Omitted when the caller has nowhere to show
	 * it, in which case no View control is rendered at all — an affordance
	 * that does nothing is worse than none.
	 */
	onSelectScan?: (reportKey: string) => void;
}

const formatWhen = (value: string | null): string => {
	if (!value) return "—";
	const parsed = new Date(value);
	return Number.isNaN(parsed.getTime())
		? value
		: parsed.toLocaleDateString(undefined, {
				year: "numeric",
				month: "short",
				day: "numeric",
			});
};

const show = (value: number | null, suffix = ""): string =>
	value === null ? "—" : `${value}${suffix}`;

function Delta({ delta }: { delta: GbpScanDelta }) {
	if (delta.change === null) {
		return <span className="text-muted-foreground">—</span>;
	}
	if (delta.improved === null) {
		return (
			<span className="inline-flex items-center gap-0.5 text-muted-foreground">
				<Minus className="h-3 w-3" />
				0
			</span>
		);
	}
	const Icon = delta.change < 0 ? ArrowDown : ArrowUp;
	const tone = delta.improved
		? "text-[hsl(var(--status-success))]"
		: "text-destructive";
	return (
		<span className={`inline-flex items-center gap-0.5 ${tone}`}>
			<Icon className="h-3 w-3" />
			{Math.abs(delta.change)}
		</span>
	);
}

function Row({
	scan,
	onSelect,
}: {
	scan: GbpScan;
	onSelect?: (reportKey: string) => void;
}) {
	return (
		<tr
			className={
				onSelect && scan.reportKey
					? "cursor-pointer border-b border-border transition-colors last:border-0 hover:bg-muted"
					: "border-b border-border last:border-0"
			}
			{...(onSelect && scan.reportKey
				? { onClick: () => onSelect(scan.reportKey) }
				: {})}
		>
			<td className="px-3 py-2 whitespace-nowrap">
				{formatWhen(scan.scannedAt)}
			</td>
			<td className="px-3 py-2">
				<span className="font-medium">{scan.keyword || "—"}</span>
				{scan.gridSize && (
					<span className="ml-1.5 text-xs text-muted-foreground">
						{scan.gridSize}×{scan.gridSize}
						{scan.radius && ` · ${scan.radius}${scan.measurement}`}
					</span>
				)}
			</td>
			<td className="px-3 py-2 whitespace-nowrap">
				{show(scan.arp)} <Delta delta={scan.deltas.arp} />
			</td>
			<td className="px-3 py-2 whitespace-nowrap">
				{show(scan.atrp)} <Delta delta={scan.deltas.atrp} />
			</td>
			<td className="px-3 py-2 whitespace-nowrap">
				{show(scan.solv, "%")} <Delta delta={scan.deltas.solv} />
			</td>
			<td className="px-3 py-2 text-muted-foreground whitespace-nowrap">
				{scan.foundIn === null || scan.dataPoints === null
					? "—"
					: `${scan.foundIn}/${scan.dataPoints}`}
			</td>
			<td className="px-3 py-2 whitespace-nowrap">
				{onSelect && scan.reportKey && (
					// A real button, not a click handler on the row: this table sits
					// directly above the profile-audit table, whose rows are NOT
					// actionable, and a whole-row target would be both ambiguous and
					// unreachable by keyboard.
					<Button
						size="sm"
						variant="ghost"
						onClick={(event) => {
							// The row is clickable too; without this, one click on
							// the button would fire the handler twice.
							event.stopPropagation();
							onSelect(scan.reportKey);
						}}
						aria-label={`View the search visibility for the ${
							scan.keyword || "unnamed"
						} scan`}
					>
						View
						<ArrowRight className="ml-1 h-3.5 w-3.5" />
					</Button>
				)}
			</td>
		</tr>
	);
}

export default function GbpScanHistory({
	tenantId,
	placeId = "",
	onSelectScan,
}: Props) {
	const { data, isLoading, isError } = useQuery<ScanHistory>({
		queryKey: ["site-health", "gbp-scan-history", tenantId, placeId],
		queryFn: () => gbpAuditService.scanHistory(tenantId, placeId),
		enabled: Boolean(tenantId),
	});

	if (isLoading) {
		return (
			<p className="text-sm text-muted-foreground py-6">Loading past scans…</p>
		);
	}

	if (isError) {
		return (
			<p className="text-sm text-destructive py-6">
				Couldn't load the scan history.
			</p>
		);
	}

	if (data?.error) {
		return <p className="text-sm text-muted-foreground py-6">{data.error}</p>;
	}

	const scans = data?.scans ?? [];
	if (scans.length === 0) {
		return (
			<p className="text-sm text-muted-foreground py-6">
				No visibility scans recorded for this listing yet.
			</p>
		);
	}

	return (
		<div className="space-y-2" data-testid="gbp-scan-history">
			<p className="text-xs text-muted-foreground">
				Rank positions improve by going <strong>down</strong>; share of local
				voice improves by going <strong>up</strong>. A scan is only compared
				with an earlier scan of the same keyword, grid and radius — otherwise
				the change is shown as “—”.
			</p>
			<div className="rounded-lg border border-border overflow-x-auto">
				<table className="w-full text-sm">
					<thead>
						<tr className="border-b border-border text-left">
							<th className="px-3 py-2 font-medium">Scanned</th>
							<th className="px-3 py-2 font-medium">Keyword</th>
							<th className="px-3 py-2 font-medium" title="Average rank position">
								ARP
							</th>
							<th
								className="px-3 py-2 font-medium"
								title="Average total rank position"
							>
								ATRP
							</th>
							<th className="px-3 py-2 font-medium" title="Share of local voice">
								SoLV
							</th>
							<th className="px-3 py-2 font-medium">Found in</th>
							<th className="px-3 py-2 font-medium">
								<span className="sr-only">View this scan</span>
							</th>
						</tr>
					</thead>
					<tbody>
						{scans.map((scan) => (
							<Row
								key={scan.reportKey || scan.scannedAt}
								scan={scan}
								onSelect={onSelectScan}
							/>
						))}
					</tbody>
				</table>
			</div>
		</div>
	);
}
